/**
 * v3.18-1325B — Operational Roles administration panel.
 *
 * Renders inside the HR Employee Edit dialog.  Lets an administrator
 * grant or revoke additional operational responsibilities WITHOUT
 * modifying the employee's HR role, title, compensation plan, or Team
 * membership.  Every mutation is audited server-side.
 *
 * Initial supported role:
 *   - operational_vo  →  authorised to be assigned as VO Officer on a deal,
 *                        even when their HR role is not "vo_officer".
 */
import { useEffect, useState, useCallback } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ROLE_LABELS = {
  operational_vo: 'Operational VO',
};
const ROLE_HELP = {
  operational_vo: 'Authorised to be selected as VO Officer on a deal, in addition to any designated VO Officer HR role.',
};

export default function OperationalRolesPanel({ userId }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy]       = useState('');
  const [state, setState]     = useState(null);

  const reload = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await api.get(`/users/${userId}/operational-roles`);
      setState(r.data);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Failed to load operational roles.'));
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = async (role, next) => {
    setBusy(role);
    try {
      const r = await api.post(`/users/${userId}/operational-roles`, {
        role, action: next ? 'grant' : 'revoke',
      });
      setState(prev => prev ? { ...prev, operational_roles: r.data.operational_roles || [] } : prev);
      toast.success(next
        ? `Granted ${ROLE_LABELS[role] || role}.`
        : `Revoked ${ROLE_LABELS[role] || role}.`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Update failed.'));
    } finally { setBusy(''); }
  };

  if (loading || !state) {
    return (
      <div className="text-xs text-slate-500 flex items-center gap-2 py-2"
           data-testid="employee-operational-roles-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading operational roles…
      </div>
    );
  }

  const current = new Set(state.operational_roles || []);
  return (
    <div className="space-y-2" data-testid="employee-operational-roles-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-indigo-600" />
        <h4 className="text-sm font-semibold text-slate-800">Operational Roles</h4>
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        These designations grant additional operational responsibilities without
        changing HR role, job title, compensation plan, or Team membership.
      </p>
      <div className="space-y-1.5">
        {(state.supported_roles || []).map(role => {
          const on   = current.has(role);
          const busyMe = busy === role;
          const label  = ROLE_LABELS[role] || role;
          const hint   = ROLE_HELP[role];
          // For operational_vo, if the user is already an HR VO Officer,
          // show a subtle note explaining they're already eligible.
          const hrExempt = role === 'operational_vo' && state.is_hr_vo;
          return (
            <label key={role}
                   className={`flex items-start gap-2 rounded border px-3 py-2 text-xs cursor-pointer ${
                     on ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:bg-slate-50'
                   }`}
                   data-testid={`employee-operational-role-${role}`}>
              <input
                type="checkbox"
                className="mt-0.5"
                checked={on}
                disabled={!!busyMe}
                onChange={e => toggle(role, e.target.checked)}
                data-testid={`employee-operational-role-toggle-${role}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{label}</span>
                  {on && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 uppercase tracking-wider">Active</span>
                  )}
                  {hrExempt && (
                    <span className="text-[9px] text-emerald-700">Already eligible via HR role.</span>
                  )}
                </div>
                {hint && (
                  <div className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{hint}</div>
                )}
              </div>
              {busyMe && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
            </label>
          );
        })}
      </div>
    </div>
  );
}
